import { useEffect, useMemo, useState } from "react"
import { ArrowUpRight, Loader2, Mail } from "lucide-react"
import { getCatalog } from "../api"
import { CONFIG } from "../config"
import type { CatalogItem, SelectedPackage } from "../types"
import { ProductCard } from "../components/ProductCard"
import { PackageGrid } from "../components/PackageGrid"
import { PackageSelector } from "../components/PackageSelector"
import { DeeplinkDialog } from "../components/DeeplinkDialog"
import { PremiumBand } from "../components/PremiumBand"
import { Benefits } from "../components/Benefits"
import { Faq } from "../components/Faq"
import { Hero } from "../components/Hero"
import { BlockHeading, FinePrint, Wrap } from "../components/ui"
import { useCart } from "../cart"
import { cardTitle, requiredPackageCount } from "../lib/format"

const CONTACT_EMAIL = "infopakete@sinus-institut.de"

// Nicht ueber Stripe kaufbar ("Preis auf Anfrage") - statische Kontakt-Kachel
// im Abschnitt Vortragsveranstaltungen (analog Bestellformular).
const WORKSHOP: CatalogItem = {
  price_id: "static:workshops",
  kind: "event",
  name: "Workshops & Deep-Dives",
  description:
    "Individuelle Kommunikationsworkshops und Deep-Dives auf Anfrage. Wir unterbreiten Ihnen gern ein individuelles Angebot.",
  amount_net: null,
  currency: "EUR",
}

function sortNum(i: CatalogItem): number {
  const n = Number(i.sort)
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER
}

export function Catalog() {
  const { add } = useCart()
  const [items, setItems] = useState<CatalogItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selectorItem, setSelectorItem] = useState<CatalogItem | null>(null)
  const [selectorInitial, setSelectorInitial] = useState<SelectedPackage[]>([])
  const [deeplink, setDeeplink] = useState<CatalogItem | null>(null)

  // Deeplink von der Produktwebsite: ?produkt=<Stripe lookup_key>
  const deeplinkKey = useMemo(
    () => new URLSearchParams(window.location.search).get("produkt"),
    [],
  )

  useEffect(() => {
    let alive = true
    getCatalog()
      .then((data) => alive && setItems(data))
      .catch((e) => alive && setError(e instanceof Error ? e.message : "Fehler"))
    return () => {
      alive = false
    }
  }, [])

  const groups = useMemo(() => {
    const all = (items ?? []).slice().sort((a, b) => sortNum(a) - sortNum(b))
    return {
      navigator: all.filter((i) => i.kind === "navigator" || (i.kind === "bundle" && i.bundle_type === "premium")),
      infopackages: all.filter((i) => i.kind === "infopackage"),
      staffel: all.filter((i) => i.kind === "bundle" && i.bundle_type === "staffel"),
      events: [...all.filter((i) => i.kind === "event"), WORKSHOP],
    }
  }, [items])

  // Sobald der Katalog da ist: verlinktes Produkt aufloesen.
  useEffect(() => {
    if (!items || !deeplinkKey) return
    const hit = items.find((i) => i.lookup_key === deeplinkKey)
    if (!hit) return
    // Navigator-Produkte und Bundles oeffnen den Dialog; Staffel fuehrt direkt
    // zur Paketauswahl, weil dort ohnehin N Pakete zu waehlen sind.
    if (hit.kind === "bundle" && hit.bundle_type === "staffel") setSelectorItem(hit)
    else if (hit.kind === "navigator" || hit.kind === "bundle") setDeeplink(hit)
    else document.getElementById(hit.kind === "event" ? "vortraege" : "infopakete")?.scrollIntoView()
  }, [items, deeplinkKey])

  function confirmPackages(selected: SelectedPackage[]) {
    if (selectorItem) add(selectorItem, selected)
    setSelectorItem(null)
    setSelectorInitial([])
  }

  if (error) {
    return (
      <Wrap className="py-24 text-center text-sm text-brand-muted">
        {error}{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-brand-blue hover:underline">
          {CONTACT_EMAIL}
        </a>
      </Wrap>
    )
  }

  if (!items) {
    return (
      <div className="flex items-center justify-center py-32 text-sm text-brand-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Katalog wird geladen …
      </div>
    )
  }

  return (
    <>
      <Hero />

      <Wrap>
        <section id="navigator" className="pb-16 md:pb-24">
          <BlockHeading title="SINUS Milieu-Navigator">
            Unsere KI-Anwendung mit dialogbasiertem Zugang zu kontinuierlich aktualisierten
            Sinus-Milieudaten, inkl. 2 Milieu Minds pro Milieu. Feste Laufzeit, kein Abo mit automatischer
            Verlängerung, keine Zusatzkosten.
            <a
              href={CONFIG.navigatorInfoUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1 font-medium text-brand-blue hover:underline"
            >
              Mehr zum Navigator erfahren <ArrowUpRight className="h-4 w-4" />
            </a>
          </BlockHeading>

          <div className="grid gap-6 lg:grid-cols-3">
            {groups.navigator.map((item) => (
              <ProductCard
                key={item.price_id}
                item={item}
                catalog={items}
                title={item.kind === "navigator" ? (item.duration === "P3M" ? "3 Monate" : "12 Monate") : item.name}
                lead={
                  item.kind === "navigator" || item.bundle_type === "premium"
                    ? `Feste Laufzeit von ${item.duration === "P3M" ? "3" : "12"} Monaten, keine automatische Verlängerung, keine Zusatzkosten.`
                    : undefined
                }
                featured={item.bundle_type === "premium"}
                onAdd={add}
                onSelectPackages={setSelectorItem}
              />
            ))}
          </div>

          <FinePrint>
            Alle Preise zzgl. gesetzlicher Mehrwertsteuer. Die aufgeführten Preise gelten für einen
            Nutzer/eine Nutzerin. Preise für weitere Lizenzen erhalten Sie auf Anfrage. Die
            angegebene Ersparnis bezieht sich auf die Jahreslizenz, den Staffelpreis für zwei
            Infopakete und den Exklusiv-Vortrag im Einzelbezug.
          </FinePrint>
        </section>

        <section id="infopakete" className="pb-16 md:pb-24">
          <BlockHeading title={<>Infopakete zu den Sinus-Milieus<sup className="align-super text-xs md:text-base">®</sup></>}>
            Dauerhafter Zugang zu einzelnen PDF-Infopaketen über das SINUS Infoportal.
          </BlockHeading>

          <h3 className="mb-5 text-lg font-medium md:text-xl">Einzelbezug der Infopakete</h3>
          <PackageGrid
            packages={groups.infopackages}
            staffel={groups.staffel}
            preselect={deeplinkKey}
            onAdd={(picked) => picked.forEach((p) => add(p))}
            onUpgrade={(staffelItem, selected) => {
              setSelectorInitial(selected)
              setSelectorItem(staffelItem)
            }}
          />
          <FinePrint>Alle Preise zzgl. gesetzlicher Mehrwertsteuer.</FinePrint>

          <h3 className="mb-2 mt-14 text-lg font-medium md:text-xl">
            Staffelpreise für den Bezug mehrerer Infopakete
          </h3>
          <p className="mb-6 text-xs text-brand-muted md:text-sm">
            Je mehr Infopakete Sie beziehen, desto größer Ihre Ersparnis. Die gewünschten Infopakete
            wählen Sie im nächsten Schritt.
          </p>
          <div className="grid gap-6 lg:grid-cols-3">
            {groups.staffel.map((item) => (
              <ProductCard
                key={item.price_id}
                item={item}
                catalog={items}
                title={`${requiredPackageCount(item)} Pakete`}
                lead="Die gewünschten Infopakete wählen Sie im nächsten Schritt."
                onAdd={add}
                onSelectPackages={setSelectorItem}
              />
            ))}
          </div>
          <FinePrint>
            Alle Preise zzgl. gesetzlicher Mehrwertsteuer. Die angegebene Ersparnis bezieht sich auf
            den Einzelbezug der Infopakete.
          </FinePrint>
        </section>
      </Wrap>

      <PremiumBand />

      <Wrap>
        <section id="vortraege" className="pb-16 md:pb-24">
          <BlockHeading title="Vortragsveranstaltungen">
            Exklusiv-Vorträge und Deep-Dives zu den Sinus-Milieus mit unseren Experten.
          </BlockHeading>
          <div className="grid gap-6 md:max-w-[846px] md:grid-cols-2">
            {groups.events.map((item) => (
              <ProductCard key={item.price_id} item={item} catalog={items} title={cardTitle(item)} onAdd={add} />
            ))}
          </div>
          <FinePrint>Alle Preise zzgl. gesetzlicher Mehrwertsteuer.</FinePrint>
        </section>

        <section id="faq" className="pb-16 md:pb-24">
          <Faq />
        </section>
      </Wrap>

      <Benefits />

      <PackageSelector
        item={selectorItem}
        requiredCount={selectorItem ? requiredPackageCount(selectorItem) : 0}
        initial={selectorInitial}
        infopackages={groups.infopackages}
        catalog={items}
        onCancel={() => {
          setSelectorItem(null)
          setSelectorInitial([])
        }}
        onConfirm={confirmPackages}
      />

      <DeeplinkDialog
        item={deeplink}
        catalog={items}
        onClose={() => setDeeplink(null)}
        onAdd={(item) => {
          setDeeplink(null)
          if (requiredPackageCount(item) > 0) setSelectorItem(item)
          else add(item)
        }}
      />

      {items.length === 0 && (
        <Wrap className="pb-24">
          <div className="rounded-card border border-line bg-surface p-8 text-center text-sm text-brand-muted">
            Derzeit sind keine Produkte verfügbar. Bei Fragen erreichen Sie uns unter{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-1 font-medium text-brand-blue hover:underline">
              <Mail className="h-3.5 w-3.5" /> {CONTACT_EMAIL}
            </a>
            .
          </div>
        </Wrap>
      )}
    </>
  )
}
